import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Button } from '@/components/Buttons';
import { ValidatingTextArea, ValidationForm, ValidationSummary } from '@/components/forms/validation';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '@ygtechnologies/bygr-frontend-component-library';
import React, { useState } from 'react';
import { OrderLine } from '@/types';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import theme from './OrderLineNoteModal.module.scss';
import Banner from '@/components/Banner';
import { saveOrderLineNote } from '@/services/orders';

interface AddNoteModalProp {
    isOpen: boolean;
    onClose(): void;
    orderLine: OrderLine;
    onSubmit(): void;
}

const OrderLineNoteModal: React.FC<AddNoteModalProp> = ({ isOpen, onClose, orderLine, onSubmit }) => {
    const [saving, setSaving] = useState(false);
    const [note, setNote] = useState('');

    const handleNoteSubmit = async (e) => {
        e.preventDefault();

        setSaving(true);

        try {
            await saveOrderLineNote(orderLine.id, note);
            triggerHandler(onClose);
            triggerHandler(onSubmit);
        } finally {
            setSaving(false);
        }
    };

    const onOpen = () => {
        if (orderLine.note) setNote(orderLine.note);
    };

    return (
        <Modal size="lg" isOpen={isOpen} toggle={onClose} closeOnEscape={false} onOpen={onOpen}>
            <ValidationForm onSubmit={handleNoteSubmit} className={theme.form}>
                <ModalHeader>
                    <Trans>Add a note</Trans>
                </ModalHeader>
                <ModalBody>
                    <div className="form-group">
                        <label>
                            <Trans>Note</Trans>
                        </label>
                        <ValidatingTextArea
                            minRows={3}
                            autoResize
                            fieldName={t`Note`}
                            required
                            className="form-control"
                            placeholder={t`Add a note to explain this order line`}
                            value={note}
                            name="note"
                            onChange={(e) => setNote(e.target.value)}
                        />
                    </div>
                    <Banner type="info">
                        <Trans>
                            <strong>Please note,</strong> this note will appear in the room form export
                        </Trans>
                    </Banner>
                    <ValidationSummary />
                </ModalBody>
                <ModalFooter>
                    <Button onClick={onClose} color="tertiary">
                        <Trans>Discard</Trans>
                    </Button>
                    <Button type="submit" color="primary" loading={saving}>
                        <Trans>Save note</Trans>
                    </Button>
                </ModalFooter>
            </ValidationForm>
        </Modal>
    );
};

export default OrderLineNoteModal;
